import type { JSX } from 'solid-js';

/**
 * The spinner, and the two places it is allowed to appear.
 *
 * These are deliberately dependency-free — no icon library, no venue context — because the controls
 * that show a busy state include the lowest-level ones in the console, and a shared primitive that
 * drags a client-only icon set behind it cannot be imported by them.
 */

/** Drawn in CSS so `prefers-reduced-motion` gets a real static ring; see `styles.css`. */
export function Spinner(props: { class?: string }): JSX.Element {
  return <span class={`spinner ${props.class ?? ''}`.trim()} aria-hidden="true" />;
}

/**
 * The spinner's place in a left-aligned READOUT — a value that changes when the room answers.
 *
 * The slot holds its 15px whether or not it is spinning, so the number beside it does not jump when the
 * system starts answering. That is right for a readout, whose content starts at a fixed left edge, and
 * wrong for a button, whose content is centred: an empty box inside a centred row pushes the label off
 * the control's centre and makes the control wider than its text. It belongs to readouts only, and a
 * `npm run check` guard (`components/busyPresentation.test.ts`) keeps it out of every button.
 */
export function BusySlot(props: { busy: boolean }): JSX.Element {
  return (
    <span class="busy-slot" classList={{ busy: props.busy }} aria-hidden="true">
      <Spinner />
    </span>
  );
}

/**
 * The spinner in a control's own place: it is drawn across the whole control while the control's
 * content — hidden, but still holding its room — sits underneath. The host is `position: relative`.
 *
 * This is how EVERY button in the console says it is working. A button reserves nothing for it, so an
 * idle button lays out exactly like a plain one, and nothing moves or resizes when it is pressed.
 */
export function BusySpinner(): JSX.Element {
  return <span class="busy-spinner"><Spinner /></span>;
}
